import { assert, Test } from "oc-e2e-testrunner";
import { resetArrangement } from "./utils";
import { getTypeNameOfPart } from "../arrangement/arrangementFunctions";
import * as Maths3 from "oc-threejs/Maths3";
import { PfostenWinkel } from "../arrangement/pfostenFunctions";
import { AngleIntervalType, MatchingRotation } from "../commons/AngleIntervals";
import { ZElement } from "../arrangement/ZElement";

export default {
  name: "TestSchiebetorDoppelpfostenUndUMProfile",
};

export const testSchiebetorDoppelpfostenUndUMProfilePlatzierung: Test = async () => {
  resetArrangement(true);
  const agt = APP.arrangement;
  agt.grundriss.setStandardForm("Eck", [5, 5]);
  agt.verteilungsstrategieAuswahlwert_ = APP.richMetadata.findAuswahlwert("StandardLinks");
  agt.update3d();
  agt.setSelectedObjects(agt.getElemente()[2]);
  agt.changeSelectedZaunElemente({
    füllungAuswahlwert: APP.richMetadata.findAuswahlwert("SYSTEM WPC PLATINUM XL Taupe-Grau"),
    massAuswahlwert: APP.richMetadata.findAuswahlwert("Schiebetor_Easy"),
  });
  const schiebetor = agt.getTore()[0];
  assert.deepEqualsPicked(
    {
      type: "Element",
      breite: 3.5,
      snap: -1,
      schieberichtungAuswahlwert: "Schieberichtung_links",
      schiebetormotorseiteAuswahlwert: "Schiebetormotorseite_A",
    },
    schiebetor.toJSON()
  );
  const doppelpfosten = schiebetor.pfostenRechts;
  // deepEqualsStringified, weil das quaternion des Pfostens an ein Object3D gebunden ist und dadurch
  // ein zusätzliches _onChangeCallback trägt, über das der rohe Deep-Equal-Vergleich stolpert.
  assert.deepEqualsStringified(
    {
      meta: "Schiebetordoppelpfosten_Anthrazit",
      angles: [
        {
          angle: 1.5708,
          flacheSeiteNotwendig: false,
          type: "",
          elementId: doppelpfosten.elemente.find((e) => e != schiebetor)?.id,
        },
        {
          angle: 3.1416,
          flacheSeiteNotwendig: true,
          type: "",
          elementId: schiebetor.id,
        },
      ],
      matchingRotation: {
        angle: 0,
        intervalOffset: 1,
        score: -0.1001,
      },
      quaternion: Maths3.quaternion.zero,
    },
    {
      meta: doppelpfosten.meta?.Key,
      angles: doppelpfosten.angles.map((a) => ({
        angle: a.angle,
        flacheSeiteNotwendig: a.flacheSeiteNotwendig,
        type: a.type,
        elementId: a.element.id,
      })),
      matchingRotation: doppelpfosten.matchingRotation,
      quaternion: doppelpfosten.quaternion,
    }
  );

  assert.deepEquals(
    [
      [
        {
          type: "Eckpfosten",
          key: "2341 SYSTEM Klemmpfosten Anthrazit 192 cm | einbetoniert",
          x: 0,
        },
        {
          type: "Element",
          key: "2143 SYSTEM WPC CLASSIC  Anthrazit/Anthrazit Zaunfeld-Set 178 x 183 cm",
          x: 0.02,
        },
        {
          type: "UMProfil",
          key: "2777 SYSTEM U-Klemmprofil Anthrazit 192 cm",
          x: 1.335,
        },
        {
          type: "SchiebetorEinzelpfosten",
          key: "Schiebetoreinzelpfosten_Anthrazit",
          x: 1.4,
        },
        {
          type: "Schiebetor",
          key: "3811 Schiebetor E-Antrieb SYSTEM WPC Platinum XL",
          x: 1.45,
        },
        {
          type: "SchiebetorDoppelpfostenEckpfosten",
          key: "Schiebetordoppelpfosten_Anthrazit",
          x: 5,
        },
      ],
      [
        {
          type: "SchiebetorDoppelpfostenEckpfosten",
          key: "Schiebetordoppelpfosten_Anthrazit",
          x: 5,
        },
        {
          type: "UMProfil",
          key: "2777 SYSTEM U-Klemmprofil Anthrazit 192 cm",
          x: 0.065,
        },
        {
          type: "Element",
          key: "2143 SYSTEM WPC CLASSIC  Anthrazit/Anthrazit Zaunfeld-Set 178 x 183 cm",
          x: 0.06,
        },
        {
          type: "Pfosten",
          key: "2341 SYSTEM Klemmpfosten Anthrazit 192 cm | einbetoniert",
          x: 1.36,
        },
        {
          type: "Element",
          key: "2143 SYSTEM WPC CLASSIC  Anthrazit/Anthrazit Zaunfeld-Set 178 x 183 cm",
          x: 1.38,
        },
        {
          type: "Pfosten",
          key: "2341 SYSTEM Klemmpfosten Anthrazit 192 cm | einbetoniert",
          x: 3.18,
        },
        {
          type: "Element",
          key: "2143 SYSTEM WPC CLASSIC  Anthrazit/Anthrazit Zaunfeld-Set 178 x 183 cm",
          x: 3.2,
        },
        {
          type: "Eckpfosten",
          key: "2341 SYSTEM Klemmpfosten Anthrazit 192 cm | einbetoniert",
          x: 5,
        },
      ],
    ],
    agt.segmente.map(dumpParts)
  );
};

export const testSchiebetorDoppelpfostenUndUMProfileLoad: Test = async () => {
  // Reale Konfiguration (SYSTEM WPC PLATINUM XL Taupe-Grau): Zwei Zaunseiten treffen an einer Ecke
  // zusammen. Auf der einen Seite steht ein Schiebetor (Schieberichtung_links) mit einem normalen
  // Zaunelement links daneben, auf der anderen Seite ein weiteres normales Zaunelement.
  const configuration = {
    inspiration: "Inspiration SYSTEM WPC PLATINUM XL Taupe-Grau",
    zaunart: "Sichtschutz",
    serie: "SYSTEM",
    grundriss: {
      ecken: [
        { id: 2131, punkt: { x: 10, y: 0 } },
        { id: 2408, punkt: { x: 10, y: 2.4597704386227743 } },
        { id: 2617, punkt: { x: 4.725637778814679, y: 0 } },
      ],
      kanten: [{ ecken: [2131, 2408] }, { ecken: [2617, 2131] }],
    },
    segmente: [
      {
        breite: 2.4598,
        partsAndPfosten: [
          {
            type: "Element",
            breite: 1.78,
            höhe: 1.845,
            snap: -1,
            isBreiteKonfigurierbar: true,
            x0: 0.05,
            füllungAuswahlwert: "SYSTEM WPC PLATINUM XL Taupe-Grau",
            füllungAuswahlwertIndividualisiert: false,
            gehflügelAuswahlwert: "Gehflügel_links",
            drückerAuswahlwert: "Drücker_Knauf_edelstahl",
            profilzylinderAuswahlwert: "Profilzylinder_mit",
            öffnungAuswahlwert: "Öffnung_nach_innen",
            anschlagAuswahlwert: "Anschlag_links",
            schieberichtungAuswahlwert: "Schieberichtung_links",
            schiebetormotorseiteAuswahlwert: "Schiebetormotorseite_A",
            füllungsseiteAuswahlwert: "Füllungsseite_B",
            anschlagprofilAuswahlwert: "Tor_an_Mauer_Anschlagsprofil_180cm",
            anschlagprofilfarbeAuswahlwert: "3574_Montageplatten Anthrazit",
            abschlussleisteAuswahlwert: "Abschlussleisten Anthrazit",
            abschlussleisteAuswahlwertIndividualisiert: false,
            profilKategorieAuswahlwert: "Dekorprofile30cm_ZaunSets",
            profilAuswahlwert: "Dekor Gamma Edelstahl/hoch",
            profilAuswahlwertIndividualisiert: false,
            profilleisteAuswahlwert: "Dekor_Adapter_anthrazit",
            zubehörAnzahl: {
              "Elektrisches Bodenverriegelungs-Set": 0,
              Handsender: 0,
              "Handsender Design": 0,
              Schlüsselschalter: 0,
              "Funk-Codeschloss": 0,
              "Bluetooth Schalter": 0,
              "Connect Gateway": 0,
              Lichtschranke: 0,
              "Techniker Key": 0,
            },
            toröffnerAuswahlwert: "Toröffner_ohne",
            torschließerAuswahlwert: "Torschließer_ohne",
            form: "Rechteck",
            massAuswahlwert: "180x180_Easy",
            profilPosition: 5,
            profilPositionKorrigiert: 5,
            profilPositionIndividualisiert: false,
            showProfil: null,
            anschlussAufRechts: false,
            gehflügelbreite: 0,
            standflügelbreite: 0,
            torIstFertig: false,
            elementNr: 0,
            isStandardMass: true,
            detailSerie: "SYSTEM WPC PLATINUM XL",
            anMauer: "AnMauer_nein",
            isMasselement: false,
            isMassProfil: false,
            isBestellseite: false,
            elementID: 0,
          },
          {
            type: "Pfosten",
            x0: 1.81,
            pfostenfarbeAuswahlwert: "Anthrazit",
            pfostenfarbeAuswahlwertIndividualisiert: false,
            bodenbefestigungAuswahlwert: "Einbetoniert im Boden",
            bodenbefestigungAuswahlwertIndividualisiert: false,
            elementhalterAuswahlwertIndividualisiert: false,
            pfostenartAuswahlwert: "Klemmpfosten",
            pfostenartAuswahlwertIndividualisiert: false,
            verbundankerAuswahlwert: "Verbundanker-Set_mit",
            ummantelungAuswahlwert: "Ummantelung_ohne",
            pfostenkappeAuswahlwert: "Pfostenkappe_inklusiv",
            ankerInnen: false,
            ausgleichGefälle: false,
          },
        ],
        verteilungsstrategieAuswahlwert: "Individuell",
      },
      {
        breite: 5.2744,
        partsAndPfosten: [
          {
            type: "Element",
            breite: 1.0944,
            höhe: 1.845,
            snap: 1,
            isBreiteKonfigurierbar: true,
            x0: 0.02,
            füllungAuswahlwert: "SYSTEM WPC PLATINUM XL Taupe-Grau",
            füllungAuswahlwertIndividualisiert: false,
            gehflügelAuswahlwert: "Gehflügel_links",
            drückerAuswahlwert: "Drücker_Knauf_edelstahl",
            profilzylinderAuswahlwert: "Profilzylinder_mit",
            öffnungAuswahlwert: "Öffnung_nach_innen",
            anschlagAuswahlwert: "Anschlag_links",
            schieberichtungAuswahlwert: "Schieberichtung_links",
            schiebetormotorseiteAuswahlwert: "Schiebetormotorseite_A",
            füllungsseiteAuswahlwert: "Füllungsseite_B",
            anschlagprofilAuswahlwert: "Tor_an_Mauer_Anschlagsprofil_180cm",
            anschlagprofilfarbeAuswahlwert: "3574_Montageplatten Anthrazit",
            abschlussleisteAuswahlwert: "Abschlussleisten Anthrazit",
            abschlussleisteAuswahlwertIndividualisiert: false,
            profilKategorieAuswahlwert: "Dekorprofile30cm_ZaunSets",
            profilAuswahlwert: "Dekor Gamma Edelstahl/hoch",
            profilAuswahlwertIndividualisiert: false,
            profilleisteAuswahlwert: "Dekor_Adapter_anthrazit",
            zubehörAnzahl: {
              "Elektrisches Bodenverriegelungs-Set": 0,
              Handsender: 0,
              "Handsender Design": 0,
              Schlüsselschalter: 0,
              "Funk-Codeschloss": 0,
              "Bluetooth Schalter": 0,
              "Connect Gateway": 0,
              Lichtschranke: 0,
              "Techniker Key": 0,
            },
            toröffnerAuswahlwert: "Toröffner_ohne",
            torschließerAuswahlwert: "Torschließer_ohne",
            form: "Rechteck",
            massAuswahlwert: "180x180_Easy",
            profilPosition: 5,
            profilPositionKorrigiert: 5,
            profilPositionIndividualisiert: false,
            showProfil: null,
            anschlussAufRechts: false,
            gehflügelbreite: 0,
            standflügelbreite: 0,
            torIstFertig: false,
            elementNr: 1,
            isStandardMass: false,
            detailSerie: "SYSTEM WPC PLATINUM XL",
            anMauer: "AnMauer_nein",
            isMasselement: false,
            isMassProfil: false,
            isBestellseite: false,
            elementID: 0,
          },
          {
            position: { x: 1.10936222118532, y: 0, z: 0 },
            quaternion: { w: 1, x: 0, y: 0, z: 0 },
            scale: { x: 1, y: 1, z: 1 },
            box: {
              min: { x: -0.015, y: 0.03813802311717174, z: -0.02000000001862645 },
              max: { x: 0.015, y: 1.9700000000000002, z: 0.02000000001862645 },
            },
            size: { x: 0.03, y: 1.9318619768828285, z: 0.0400000000372529 },
            type: "UMProfil",
            meta: "2777 SYSTEM U-Klemmprofil Anthrazit 192 cm",
          },
          {
            type: "Pfosten",
            x0: 1.1244,
            pfostenfarbeAuswahlwert: "Anthrazit",
            pfostenfarbeAuswahlwertIndividualisiert: false,
            bodenbefestigungAuswahlwertIndividualisiert: false,
            elementhalterAuswahlwertIndividualisiert: false,
            pfostenartAuswahlwertIndividualisiert: false,
            verbundankerAuswahlwert: "Verbundanker-Set_mit",
            ummantelungAuswahlwert: "Ummantelung_ohne",
            ankerInnen: false,
            ausgleichGefälle: false,
          },
          {
            type: "Element",
            breite: 4,
            höhe: 1.8,
            snap: 1,
            isBreiteKonfigurierbar: true,
            x0: 1.2244,
            füllungAuswahlwert: "SYSTEM WPC PLATINUM XL Taupe-Grau",
            füllungAuswahlwertIndividualisiert: false,
            gehflügelAuswahlwert: "Gehflügel_links",
            rahmenAuswahlwert: "Schiebetor_Rahmen_Anthrazit",
            öffnungAuswahlwert: "Öffnung_nach_innen",
            anschlagAuswahlwert: "Anschlag_links",
            schieberichtungAuswahlwert: "Schieberichtung_links",
            schiebetormotorseiteAuswahlwert: "Schiebetormotorseite_A",
            füllungsseiteAuswahlwert: "Füllungsseite_B",
            anschlagprofilAuswahlwert: "Tor_an_Mauer_Anschlagsprofil_180cm",
            anschlagprofilfarbeAuswahlwert: "3574_Montageplatten Anthrazit",
            abschlussleisteAuswahlwertIndividualisiert: false,
            profilKategorieAuswahlwert: "Dekorprofile_Mass_Tore_30cm",
            profilAuswahlwert: "Dekor Gamma Edelstahl/hoch",
            profilAuswahlwertIndividualisiert: false,
            profilleisteAuswahlwert: "Dekor_Adapter_anthrazit",
            zubehörAnzahl: {
              "Elektrisches Bodenverriegelungs-Set": 0,
              Handsender: 0,
              "Handsender Design": 0,
              Schlüsselschalter: 0,
              "Funk-Codeschloss": 0,
              "Bluetooth Schalter": 0,
              "Connect Gateway": 0,
              Lichtschranke: 0,
              "Techniker Key": 0,
            },
            toröffnerAuswahlwert: "Toröffner_ohne",
            torschließerAuswahlwert: "Torschließer_ohne",
            form: "Schiebetor",
            massAuswahlwert: "Schiebetor_Easy",
            profilPosition: 6,
            profilPositionKorrigiert: 6,
            profilPositionIndividualisiert: false,
            showProfil: null,
            anschlussAufRechts: false,
            gehflügelbreite: 0,
            standflügelbreite: 0,
            torIstFertig: false,
            elementNr: 2,
            isStandardMass: false,
            detailSerie: "SYSTEM WPC PLATINUM XL",
            anMauer: "AnMauer_nein",
            isMasstor: true,
            isMasselement: false,
            isMassProfil: false,
            isBestellseite: false,
            isEAntrieb: true,
            key: "3811 Schiebetor E-Antrieb SYSTEM WPC Platinum XL",
            lichteWeite: 4,
            hideRahmen: false,
            showProfilausrichtung: true,
            showAdapterleiste: true,
            hasAnschlagprofil: false,
            bestelllisteArtikel: [],
            artikelID: "3811_wpc_platinum",
            füllungName: "Füllung",
            ausrichtungCode: "sal",
            profilAnzahl: 1,
            pdf: {},
            elementID: 0,
          },
        ],
        verteilungsstrategieAuswahlwert: "Individuell",
      },
    ],
    pfostenartAuswahlwert: "Klemmpfosten",
    pfostenfarbeAuswahlwert: "Anthrazit",
    bodenbefestigungAuswahlwert: "Einbetoniert im Boden",
    elementhalterAuswahlwert: "2096 Elementhalter",
    verteilungsstrategieAuswahlwert: "StandardLinks",
    pageName: "Zauneditor",
    hinweistexte: ["WORKFLOW_Schiebetor"],
    zauneditorVisited: true,
  };

  resetArrangement(true);
  const agt = APP.arrangement;
  agt.arrangement.fromJSON(configuration);
  agt.update3d();
  const schiebetor = agt.getTore()[0];
  assert.deepEqualsPicked(
    {
      type: "Element",
      breite: 4,
      snap: 1,
      schieberichtungAuswahlwert: "Schieberichtung_links",
      schiebetormotorseiteAuswahlwert: "Schiebetormotorseite_A",
    },
    schiebetor.toJSON()
  );
  assert.deepEquals(
    [
      [
        // Der Pfosten bei x=10 steht auf Ecke 2131 und gehört damit zu beiden Segmenten:
        // er taucht hier und am Ende von Segment 1 als dasselbe Objekt auf.
        {
          type: "SchiebetorDoppelpfostenEckpfosten",
          key: "Schiebetordoppelpfosten_Anthrazit",
          x: 10,
        },
        {
          type: "UMProfil",
          key: "2777 SYSTEM U-Klemmprofil Anthrazit 192 cm",
          x: 0.065,
        },
        {
          type: "Element",
          key: "3685 SYSTEM WPC PLATINUM XL Taupe-Grau/Anthrazit Zaunfeld-Set 178 x 184 cm",
          x: 0.06,
        },
        {
          type: "Pfosten",
          key: "2341 SYSTEM Klemmpfosten Anthrazit 192 cm | einbetoniert",
          x: 1.86,
        },
        {
          type: "Empty",
          key: null,
          x: 1.9,
        },
      ],
      [
        {
          type: "Eckpfosten",
          key: "2341 SYSTEM Klemmpfosten Anthrazit 192 cm | einbetoniert",
          x: 4.726,
        },
        {
          type: "Element",
          key: "3685 SYSTEM WPC PLATINUM XL Taupe-Grau/Anthrazit Zaunfeld-Set 178 x 184 cm",
          x: 0.02,
        },
        {
          type: "UMProfil",
          key: "2777 SYSTEM U-Klemmprofil Anthrazit 192 cm",
          x: 1.109,
        },
        {
          type: "SchiebetorEinzelpfosten",
          key: "Schiebetoreinzelpfosten_Anthrazit",
          x: 1.174,
        },
        {
          type: "Schiebetor",
          key: "3811 Schiebetor E-Antrieb SYSTEM WPC Platinum XL",
          x: 1.224,
        },
        {
          type: "SchiebetorDoppelpfostenEckpfosten",
          key: "Schiebetordoppelpfosten_Anthrazit",
          x: 10,
        },
      ],
    ],
    agt.segmente.map(dumpParts)
  );

  /** Liefert eine kompakte, gut lesbare Beschreibung aller Parts/Pfosten eines Segments. */
};

function dumpParts(segment) {
  return segment.getPartsAndPfosten().map((p, i) => ({
    type: getTypeNameOfPart(p),
    key: (p.meta as any)?.Key ?? null,
    x: Math.round((p.position?.x ?? NaN) * 1000) / 1000,
  }));
}
